import posthog from 'posthog-js';

import { clientCookies } from '@/shared/constants/constants';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { useUserStore } from '@/shared/pinia/modules/user';
import { clearCookie, getCookie, setCookie } from '@/shared/utils/cookie';
import { hashUserData } from '@/shared/utils/tracking';

import { buildUserPropertiesWithConsent } from './consent';
import type { PostHogUserProperties } from './types';

let pendingIdentityReset = false;

export const getStoredUserId = (): string | null => {
  return getCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID) ?? null;
};

export const buildUserProperties = (): PostHogUserProperties => {
  const userStore = useUserStore();
  const configStore = useConfigStore();

  const alwaysTrackedProperties: PostHogUserProperties = {
    is_logged_in: userStore.isLogged
  };

  const consentTrackedProperties: PostHogUserProperties = {
    country: configStore.countryCode,
    language: configStore.language
  };

  return buildUserPropertiesWithConsent(
    alwaysTrackedProperties,
    consentTrackedProperties
  );
};

export const refreshIdentityCookieStorage = (): void => {
  const userStore = useUserStore();

  if (userStore.customer?.uuid) {
    setCookie(
      clientCookies.PRODUCT_ANALYTICS_USER_ID,
      hashUserData(userStore.customer.uuid),
      365
    );
    return;
  }

  // User is not logged in - check if we need to clear stored identity
  if (getCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID)) {
    clearCookie(clientCookies.PRODUCT_ANALYTICS_USER_ID);
    pendingIdentityReset = true;
  }
};

export const identifyUser = (distinctId: string): void => {
  if (posthog.get_distinct_id() !== distinctId) {
    posthog.identify(distinctId, buildUserProperties());
  }
};

export const resetIdentityOnLogout = (): void => {
  if (pendingIdentityReset) {
    posthog.reset();
    pendingIdentityReset = false;
  }
};
